import { defineStore } from 'pinia';
import { getPositionTotal, getWalletBalances } from '@/api/trade';
import { fetchPlatformBalance, invalidatePlatformBalanceCache } from '@/api/withdraw';
import { validateSession } from '@/composables/useAuth';
import {
  computePlatformAvailableDisplay,
  computePortfolioTotalAssets,
  readCombinedCollateralAvailable,
  readPlatformAvailable,
  readPlatformTotal,
  readPositionTotalField,
} from '@/domain/user/portfolio-totals';
import {
  fetchUserWalletBalanceSummary,
  invalidateUsdcBalanceCache,
} from '@/utils/wallet-balance-summary';
import { PLATFORM_WALLET_CURRENCY } from '@/utils/trade-contracts';

/** 充值 / 提现 / 成交 / 撤单 / 赎回 / 积分兑换后：轮询 wallet/detail?currency=DUSD */
const POST_TRADE_FAST_MS = 2500;
/** 轮询窗口 3 分钟（入账 / 出金可能有延迟） */
const POST_TRADE_FAST_WINDOW_MS = 180_000;
/** 相对 baseline 连续多少票仍变化后停止 FAST 轮询（每次拉取都会立刻 commit 到 UI） */
const POST_TRADE_STABLE_TICKS = 2;
const BALANCE_EPS = 0.0001;

let watchTimer = null;
let watchStartedAt = 0;
let watchStableTicks = 0;
let refreshInflight = null;
let refreshInflightForce = false;
/** 开始轮询时的平台可用快照 */
let tradeWatchBaselineAvailable = null;

function stopPostTradeWatchTimer() {
  if (watchTimer) {
    clearTimeout(watchTimer);
    watchTimer = null;
  }
  watchStartedAt = 0;
  watchStableTicks = 0;
  tradeWatchBaselineAvailable = null;
}

function readCashAvailable(store) {
  const n = Number(store.platformAvailable);
  return Number.isFinite(n) ? n : 0;
}

/** 平台 DUSD 可用是否相对 baseline 已变化（不再区分买卖 / 持仓双变） */
function isPlatformAvailableChanged(baselineAvailable, nextAvailable) {
  if (!Number.isFinite(baselineAvailable) || !Number.isFinite(nextAvailable)) return false;
  return Math.abs(nextAvailable - baselineAvailable) > BALANCE_EPS;
}

function scheduleWatchTick(store) {
  watchTimer = setTimeout(() => {
    void runWatchTick(store);
  }, POST_TRADE_FAST_MS);
}

function finishTradeWatch(store) {
  stopPostTradeWatchTimer();
  store.watchPhase = 'idle';
}

async function runWatchTick(store) {
  watchTimer = null;
  if (store.watchPhase !== 'fast') return;

  if (document.visibilityState !== 'hidden') {
    await store.refresh({ force: true, silent: true });

    // 每次 refresh 已 commit；用 store 现价判断是否相对 baseline 入账，仅决定何时停轮询
    const currentAvailable = readCashAvailable(store);
    const ready =
      tradeWatchBaselineAvailable != null
        ? isPlatformAvailableChanged(tradeWatchBaselineAvailable, currentAvailable)
        : false;

    if (ready) {
      watchStableTicks += 1;
    } else {
      watchStableTicks = 0;
    }

    if (ready && watchStableTicks >= POST_TRADE_STABLE_TICKS) {
      finishTradeWatch(store);
      return;
    }
  }

  if (Date.now() - watchStartedAt >= POST_TRADE_FAST_WINDOW_MS) {
    finishTradeWatch(store);
    return;
  }

  scheduleWatchTick(store);
}

export const usePortfolioBalanceStore = defineStore('portfolioBalance', {
  state: () => ({
    /** @deprecated 链上 USDC 仅作充值 Connected 参考；总资产/余额以 platformAvailable 为准 */
    chainUsdc: null,
    platformAvailable: null,
    platformDetail: null,
    positionTotal: null,
    walletSummary: null,
    loading: false,
    lastFetchedAt: null,
    watchPhase: 'idle',
  }),

  getters: {
    positionValue(state) {
      return readPositionTotalField(state.positionTotal, 'totalValue');
    },

    platformCash(state) {
      return readPositionTotalField(state.positionTotal, 'balance');
    },

    /** 已加载平台账本（用于 … 占位） */
    isPlatformBalanceReady(state) {
      return state.platformAvailable != null;
    },

    totalAssets(state) {
      return computePortfolioTotalAssets({
        platformAvailable: state.platformAvailable,
        platformTotal: state.platformDetail?.total,
        positionTotal: state.positionTotal,
      });
    },

    platformAvailableDisplay(state) {
      return computePlatformAvailableDisplay({
        platformAvailable: state.platformAvailable,
      });
    },

    /** 交易现金：平台 DUSD 可用 */
    tradeCashBalance(state) {
      return computePlatformAvailableDisplay({
        platformAvailable: state.platformAvailable,
      });
    },

    walletBalanceRows(state) {
      const list = Array.isArray(state.walletSummary?.wallets) ? state.walletSummary.wallets : [];
      return list.map((row) => ({
        address: row.address,
        providerId: row.providerId,
        balance: Number(row.balance ?? 0),
      }));
    },
  },

  actions: {
    invalidateCaches() {
      invalidatePlatformBalanceCache();
      invalidateUsdcBalanceCache();
    },

    stopWatch() {
      finishTradeWatch(this);
    },

    reset() {
      finishTradeWatch(this);
      this.chainUsdc = null;
      this.platformAvailable = null;
      this.platformDetail = null;
      this.positionTotal = null;
      this.walletSummary = null;
      this.loading = false;
      this.lastFetchedAt = null;
    },

    commitFetchResults({ chainSummary, positionRes, balanceRes, balancesRes }) {
      const chainTotal = Number(chainSummary?.total ?? chainSummary?.active?.balance ?? 0);
      this.chainUsdc = Number.isFinite(chainTotal) ? chainTotal : 0;
      this.walletSummary = chainSummary ?? null;
      this.positionTotal = positionRes && typeof positionRes === 'object' ? positionRes : null;

      if (balancesRes && typeof balancesRes === 'object') {
        const combined = readCombinedCollateralAvailable(balancesRes);
        const dusdOnly = Number(balancesRes?.dusd ?? 0);
        const activityOnly = Number(balancesRes?.activityDusd ?? 0);
        this.platformAvailable = Number.isFinite(combined)
          ? combined
          : readPlatformAvailable(balanceRes);
        // 总资产现金须含 ACTIVITY_DUSD：不可再用 wallet/detail?currency=DUSD 的 total（会漏体验金）。
        const dusdFrozen = Number(balanceRes?.frozen ?? 0);
        const frozen = Number.isFinite(dusdFrozen) ? dusdFrozen : 0;
        const cashTotal = Number.isFinite(this.platformAvailable)
          ? this.platformAvailable + frozen
          : readPlatformTotal(balanceRes);
        this.platformDetail = {
          available: this.platformAvailable,
          frozen,
          total: cashTotal,
          currency: balanceRes?.currency ?? PLATFORM_WALLET_CURRENCY,
          tokenId: balanceRes?.tokenId ?? '',
          dusdAvailable: Number.isFinite(dusdOnly) ? dusdOnly : 0,
          activityDusdAvailable: Number.isFinite(activityOnly) ? activityOnly : 0,
        };
      } else if (balanceRes && typeof balanceRes === 'object') {
        this.platformAvailable = readPlatformAvailable(balanceRes);
        this.platformDetail = {
          available: this.platformAvailable,
          frozen: Number(balanceRes.frozen ?? 0),
          total: readPlatformTotal(balanceRes),
          currency: balanceRes.currency ?? PLATFORM_WALLET_CURRENCY,
          tokenId: balanceRes.tokenId ?? '',
        };
      } else {
        this.platformAvailable = 0;
        this.platformDetail = null;
      }
    },

    applyFetchResults(payload) {
      // 始终立刻 commit：顶栏与个人中心共用同一 store，避免 FAST 暂存导致一边先变一边后变
      this.commitFetchResults(payload);
    },

    async refresh({ force = false, silent = false } = {}) {
      if (!validateSession()) {
        this.reset();
        return;
      }

      if (force) {
        this.invalidateCaches();
      }

      if (refreshInflight) {
        if (force && !refreshInflightForce) {
          await refreshInflight;
          return this.refresh({ force, silent });
        }
        return refreshInflight;
      }

      if (!silent || this.platformAvailable == null) {
        this.loading = true;
      }

      refreshInflightForce = force;

      const task = (async () => {
        try {
          const [balancesRes, balanceRes, chainSummary, positionRes] = await Promise.all([
            getWalletBalances().catch((err) => {
              console.warn('[portfolio-balance] getWalletBalances failed', err);
              return null;
            }),
            fetchPlatformBalance(PLATFORM_WALLET_CURRENCY, { force }).catch((err) => {
              console.warn('[portfolio-balance] fetchPlatformBalance failed', err);
              return null;
            }),
            // 链上 USDC 仍拉取：Connected 充值等场景参考，不计入总资产/余额
            fetchUserWalletBalanceSummary().catch((err) => {
              console.warn('[portfolio-balance] fetchUserWalletBalanceSummary failed', err);
              return null;
            }),
            getPositionTotal().catch((err) => {
              console.warn('[portfolio-balance] getPositionTotal failed', err);
              return null;
            }),
          ]);

          this.applyFetchResults({ chainSummary, positionRes, balanceRes, balancesRes });
          this.lastFetchedAt = Date.now();
        } finally {
          this.loading = false;
          refreshInflight = null;
          refreshInflightForce = false;
        }
      })();

      refreshInflight = task;
      return task;
    },

    /**
     * 充值 / 提现 / 成交 / 撤单后：FAST 轮询 wallet/detail?currency=DUSD。
     * 每次拉取立刻写入 store（顶栏 / 个人中心同步变）；相对 baseline 稳定变化后停止轮询。
     * @param {{ side?: string|null }} [_options] 兼容旧调用；side 已忽略
     */
    refreshAfterTrade(_options = {}) {
      finishTradeWatch(this);
      this.invalidateCaches();

      tradeWatchBaselineAvailable = readCashAvailable(this);
      watchStartedAt = Date.now();
      watchStableTicks = 0;
      this.watchPhase = 'fast';
      // 立即拉一票，避免首屏再空等 POST_TRADE_FAST_MS；之后仍按间隔轮询
      void runWatchTick(this);
    },
  },
});
